import { useCallback, useEffect, useState } from "react";
import { loadIdentity, loadIdentityFromNative, clearIdentity } from "../lib/identity";
import { bootstrapPublicSession, clearSigningCache } from "../lib/signedRequest";
import { clearDmKeyCache } from "../lib/dmCrypto";
import { deleteCurrentProfile } from "../lib/accountApi";
import { devDebug } from "../lib/devLog";
import { requestPersistentKeyStorage } from "../lib/keystore";

const RETENTION_PENDING_PREFIX = "ol.retentionNotice.pending.";
const RETENTION_ACCEPTED_PREFIX = "ol.retentionNotice.accepted.";

function identityIdOf(identity) {
  return typeof identity?.identityId === "string" ? identity.identityId : "";
}

function noticeIsPending(identity) {
  const id = identityIdOf(identity);
  if (!id) return false;
  try {
    return localStorage.getItem(`${RETENTION_PENDING_PREFIX}${id}`) === "1"
      && !localStorage.getItem(`${RETENTION_ACCEPTED_PREFIX}${id}`);
  } catch {
    return false;
  }
}

function allowsLocalProfileReset(error) {
  const message = String(error?.message || "");
  return error?.code === "PUBLIC_SESSION_EXPIRED"
    || /impossibile sbloccare la chiave privata/i.test(message)
    || /sessione scaduta o non valida/i.test(message);
}

export function useUserSession() {
  const [identity, setIdentity] = useState(() => loadIdentity());
  const [route, setRoute] = useState(() => {
    const stored = loadIdentity();
    if (!stored) return "loading";
    return noticeIsPending(stored) ? "retention-notice" : "home";
  });

  // Android keeps an encrypted native mirror because some WebView versions
  // may recreate or evict localStorage when the app process is closed.
  useEffect(() => {
    if (route !== "loading") return;
    let active = true;
    void loadIdentityFromNative().then((restored) => {
      if (!active) return;
      if (restored?.identityId) {
        setIdentity(restored);
        setRoute(noticeIsPending(restored) ? "retention-notice" : "home");
      } else {
        setRoute("register");
      }
    });
    return () => { active = false; };
  }, [route]);

  // Existing installations ask the browser to keep their cryptographic store
  // persistent before renewals or app updates can put storage under pressure.
  useEffect(() => {
    if (!identityIdOf(identity)) return;
    void requestPersistentKeyStorage();
  }, [identity]);

  // Existing profiles automatically renew the HttpOnly social session when
  // the app starts. Public actions themselves never reopen the private key.
  useEffect(() => {
    if (!identityIdOf(identity)) return;
    void bootstrapPublicSession().catch((error) => {
      devDebug("publicSession: apertura automatica non riuscita", error?.message);
    });
  }, [identity]);

  const completeRegistration = useCallback(async (u) => {
    const registered = { ...u, ...(loadIdentity() || {}) };
    setIdentity(registered);
    const id = identityIdOf(registered);
    if (id) {
      try {
        localStorage.setItem(`${RETENTION_PENDING_PREFIX}${id}`, "1");
        localStorage.removeItem(`${RETENTION_ACCEPTED_PREFIX}${id}`);
      } catch {
        // The route gate still shows the notice in the current session.
      }
    }
    // Enter the next screen immediately. Updating `identity` triggers the
    // bootstrap effect above; awaiting a second bootstrap here caused Android
    // WebView to remain on "Profilo creato" until the app was restarted.
    // The legal gate performs one safe bootstrap retry if the session cookie
    // is not visible yet.
    setRoute("retention-notice");
  }, []);

  const acknowledgeRetentionNotice = useCallback(() => {
    const id = identityIdOf(identity);
    if (id) {
      try {
        localStorage.setItem(`${RETENTION_ACCEPTED_PREFIX}${id}`, new Date().toISOString());
        localStorage.removeItem(`${RETENTION_PENDING_PREFIX}${id}`);
      } catch {
        // Continue after an explicit acknowledgement even if storage is unavailable.
      }
    }
    setRoute("home");
  }, [identity]);

  const resetLocalDevice = useCallback(async () => {
    const id = identityIdOf(loadIdentity());
    clearSigningCache();
    clearDmKeyCache();
    await clearIdentity();
    if (id) {
      try {
        localStorage.removeItem(`${RETENTION_PENDING_PREFIX}${id}`);
        localStorage.removeItem(`${RETENTION_ACCEPTED_PREFIX}${id}`);
      } catch {
        // Identity data has already been cleared.
      }
    }
    setIdentity(null);
    setRoute("register");
  }, []);

  const logout = useCallback(() => {
    void resetLocalDevice();
  }, [resetLocalDevice]);

  const deleteProfile = useCallback(async () => {
    try {
      await deleteCurrentProfile();
      await resetLocalDevice();
    } catch (error) {
      if (!allowsLocalProfileReset(error)) throw error;

      const removeOnlyFromDevice = window.confirm(
        "La sessione o la chiave di questo profilo di prova non sono più utilizzabili, quindi il server non può verificarne la cancellazione.\n\nVuoi rimuovere comunque il profilo da questo dispositivo e creare subito una nuova identità? Il vecchio profilo resterà sul server finché non verrà eliminato dall’amministratore.",
      );
      if (!removeOnlyFromDevice) throw error;
      await resetLocalDevice();
    }
  }, [resetLocalDevice]);

  return {
    route,
    setRoute,
    user: identity,
    completeRegistration,
    acknowledgeRetentionNotice,
    logout,
    deleteProfile,
  };
}
